import React, { useState, useEffect } from 'react'
import * as R from 'ramda'
import {
  TitlePrimary,
  ParagraphSecondary,
  TitleSecondary,
  SpacerVertical
} from 'mage-deps/@klarna/bubble-ui'
import KeyValueList from './KeyValueList'
import { useSelector } from 'react-redux'
import { components as coreComponents } from 'mage-core'
import PropTypes from 'prop-types'
import PhoneContact from './PhoneContact'
import ChatContact from './ChatContact'
import EmailContact from './EmailContact'
import { Message } from 'mage-i18n'
import { EducationAnalytics, useEducationFeatures } from 'education-tools'
import { selectors as conversationSelectors } from 'mage-conversation'

const phoneNumberByMarket = {
  DE: '+49 221 66950130',
  SE: '+46 8 12012030',
  GB: '+44 20 30050844',
  US: '+1 844-KLARNA0',
  AT: '+43 720 883820',
  NL: '+31 20 8082853',
  NO: '+47 21 049600',
  DK: '+45 69 918883',
  BE: '+31 20 8082853'
}

const emailAddresses = {
  DE: 'shop@klarna.de',
  SE: 'merchant@klarna.se',
  GB: 'merchant@klarna.co.uk',
  US: 'merchant.usa@klarna.com',
  NL: 'webwinkel@klarna.nl',
  BE: 'webwinkel@klarna.nl',
  NO: 'merchant@klarna.no',
  DK: 'merchant@klarna.dk',
  FI: 'kauppa@klarna.fi'
}

const ContactPage = ({ parameters, merchantIds, email }) => {
  const markets = useSelector(conversationSelectors.markets)

  const businessDetails = [
    {
      key: <Message path="hqm.contact.businessDetailsEmail" fallback="Email" />,
      value: email
    }
  ]

  // only show mid for merchants with a single MID
  if (merchantIds.length === 1) {
    businessDetails.push({
      key: <Message path="hqm.contact.businessDetailsMid" fallback="Merchant ID" />,
      value: merchantIds[0]
    })
  }

  const [isChatAvailable, setChatAvailable] = useState(false)
  useEffect(() => {
    setChatAvailable(window.fcWidget && window.fcWidget.isInitialized())
  }, [])

  const phoneNumbers = Object.values(R.pick(markets, phoneNumberByMarket))
  const isPhoneAvailable = phoneNumbers.length > 0

  const emailAddress = emailAddresses[markets[0]] || 'merchant@klarna.com'

  const features = useEducationFeatures()

  if (!features) return null

  const showChat = isChatAvailable && features.uhqm_contact_chat
  const showPhone = isPhoneAvailable && features.uhqm_contact_phone
  const showEmail = !showChat && !showPhone

  return (
    <div>
      <EducationAnalytics
        category="uhqm/page/contact"
        action="pageview"
        label={parameters.articleTitle}
        referenceType={parameters.articleId ? 'article' : undefined}
        referenceId={parameters.articleId}
      />
      <TitlePrimary id="uhqm-contact-title">
        <Message path="hqm.contact.title" fallback="Contact us" />
      </TitlePrimary>
      <SpacerVertical small />
      <ParagraphSecondary>
        <Message
          path="hqm.contact.channelsIntro"
          fallback="Reach out to us so that we can help you. "
        />
      </ParagraphSecondary>
      <SpacerVertical large />

      {showChat && <ChatContact articleId={parameters.articleId} />}
      {showPhone && <PhoneContact articleId={parameters.articleId} phoneNumbers={phoneNumbers} />}
      {showEmail && <EmailContact articleId={parameters.articleId} emailAddress={emailAddress} />}

      <TitleSecondary>
        <Message path="hqm.contact.businessDetailsTitle" fallback="Your business details" />
      </TitleSecondary>
      <KeyValueList items={businessDetails} />
    </div>
  )
}

ContactPage.propTypes = {
  parameters: PropTypes.object.isRequired,
  merchantIds: PropTypes.arrayOf(PropTypes.string).isRequired,
  email: PropTypes.string.isRequired
}

export default R.compose(
  coreComponents.withTokenHelper(tokenHelper => ({
    merchantIds: tokenHelper.getMids(),
    email: tokenHelper.getEmail()
  }))
)(ContactPage)
